import { useRouter } from '@tanstack/react-router'

import { useState } from 'react'

import { useAuth } from '@/lib/auth'
import { useDb } from '@/lib/dbContext'
import { useBranding } from '@/lib/branding'
import { formatPocketBaseError } from '@/lib/pocketbaseErrors'
import {
  ProfileScreen,
  type ProfileFormValues,
  type ResetPasswordValues,
} from '@/screens/ProfileScreen'

export function ProfileScreenContainer() {
  const router = useRouter()
  const db = useDb()
  const brand = useBranding()
  const { user, setUser, logout } = useAuth()
  const [profileErrorMessage, setProfileErrorMessage] =
    useState<string | null>(null)
  const [passwordErrorMessage, setPasswordErrorMessage] =
    useState<string | null>(null)

  const handleUpdateProfile = async (
    values: ProfileFormValues,
  ) => {
    if (!user) return

    setProfileErrorMessage(null)
    try {
      const updatedUser = await db.updateProfile(values)
      setUser(updatedUser)
    } catch (error) {
      setProfileErrorMessage(
        formatPocketBaseError(error, {
          fallbackMessage: 'Failed to update your profile.',
        }),
      )
      throw error
    }
  }

  const handleResetPassword = async (
    values: ResetPasswordValues,
  ) => {
    setPasswordErrorMessage(null)
    try {
      await db.updatePassword(values)
    } catch (error) {
      setPasswordErrorMessage(
        formatPocketBaseError(error, {
          fallbackMessage: 'Failed to update your password.',
        }),
      )
      throw error
    }
  }

  const handleLogout = async () => {
    await logout()
    router.navigate({ to: '/login' })
  }

  return (
    <ProfileScreen
      user={user ?? undefined}
      onUpdateProfile={handleUpdateProfile}
      onResetPassword={handleResetPassword}
      onLogout={handleLogout}
      onProfileEditStart={() => setProfileErrorMessage(null)}
      onProfileCancel={() => setProfileErrorMessage(null)}
      onPasswordEditStart={() => setPasswordErrorMessage(null)}
      onPasswordCancel={() => setPasswordErrorMessage(null)}
      profileErrorMessage={profileErrorMessage}
      passwordErrorMessage={passwordErrorMessage}
      brandName={brand.name}
    />
  )
}
